import React from "react";
import styled from "styled-components";
import { Button, H2, Popup, SkubotSpinner, XIcon } from "@commonsku/styles";
import { BASE_ZINDEX } from "../popup-factory";
import useGlobalKeyboardEvent from "../hooks/useGlobalKeyboardEvent";

const DEFAULT_POPUP_STYLE: React.CSSProperties = {
    width: 640,
    height: "fit-content",
    borderRadius: 10,
    padding: 0,
};

const Panel = styled.div`
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const HeaderRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
`;

const Footer = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 10px;
`;

const ConfirmButtonContent = styled.span`
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
`;

const ConfirmButtonLabel = styled.span<{ $loading: boolean }>`
    visibility: ${({ $loading }) => ($loading ? "hidden" : "visible")};
`;

const ConfirmButtonSpinner = styled.span`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
`;

export interface ConfirmPopupProps {
    title?: string | null;
    titleStyle?: React.CSSProperties;
    cancelLabel?: string;
    confirmLabel?: string;
    confirmLoading?: boolean;
    confirmVariant?: "primary" | "secondary" | "error";
    confirmDisabled?: boolean;
    onClose?: () => void;
    onConfirm: () => void;
    children: React.ReactNode;
    index?: number;
    style?: React.CSSProperties;
    showCloseIcon?: boolean;
}

const ConfirmPopup = ({
    title = null,
    titleStyle,
    cancelLabel = "Cancel",
    confirmLabel = "Confirm",
    confirmLoading = false,
    confirmVariant = "primary",
    confirmDisabled = false,
    onClose,
    onConfirm,
    children,
    index = 0,
    style,
    showCloseIcon = false,
}: ConfirmPopupProps) => {
    // Keep the global keyboard event hook from HEAD
    useGlobalKeyboardEvent("Escape", onClose ?? onConfirm);

    return (
        <Popup
            overlayZIndex={BASE_ZINDEX + index + 1}
            zIndex={BASE_ZINDEX + index + 2}
            style={{ ...DEFAULT_POPUP_STYLE, ...style }}
            noHeader
        >
            <Panel>
                {/* Merge the HeaderRow from deployment with the titleStyle from HEAD */}
                {title ? (
                    showCloseIcon && onClose ? (
                        <HeaderRow>
                            <H2 style={{ fontSize: 24, lineHeight: "40px", margin: 0, flex: 1, ...titleStyle }}>{title}</H2>
                            <XIcon cursor="pointer" onClick={onClose} size="large" />
                        </HeaderRow>
                    ) : (
                        <H2 style={{ fontSize: 24, lineHeight: "40px", margin: 0, ...titleStyle }}>{title}</H2>
                    )
                ) : null}

                {children}

                <Footer>
                    {onClose && (
                        <Button variant="secondary" size="medium" onClick={onClose}>
                            {cancelLabel}
                        </Button>
                    )}
                    {/* Keep the advanced confirm button with loading states from HEAD */}
                    <Button
                        variant={confirmDisabled ? "disabled" : confirmVariant}
                        size="medium"
                        onClick={!confirmDisabled && !confirmLoading ? onConfirm : undefined}
                        disabled={confirmDisabled}
                    >
                        <ConfirmButtonContent>
                            <ConfirmButtonLabel $loading={confirmLoading}>
                                {confirmLabel}
                            </ConfirmButtonLabel>
                            {confirmLoading ? (
                                <ConfirmButtonSpinner>
                                    <SkubotSpinner
                                        size="button"
                                        color="white"
                                        skubot={false}
                                        spinnerSize={24}
                                        containerSize={24}
                                    />
                                </ConfirmButtonSpinner>
                            ) : null}
                        </ConfirmButtonContent>
                    </Button>
                </Footer>
            </Panel>
        </Popup>
    );
};

export default ConfirmPopup;
